import React, { useState, useEffect, useRef, useCallback } from "react";
import { ref, getDatabase, get } from "firebase/database";

function TablePickerPopup({ ownerId, onSelect, onNoTable, onCancel }) {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canScrollMore, setCanScrollMore] = useState(false);
  const scrollRef = useRef(null);
  const db = getDatabase();

  useEffect(() => {
    if (!ownerId) { setLoading(false); return; }
    get(ref(db, `tables/${ownerId}`)).then((snap) => {
      if (snap.exists()) {
        setTables(Object.values(snap.val()));
      }
      setLoading(false);
    });
  }, [db, ownerId]);

  // Track whether there's more content below the fold so we can show a
  // "scroll for more" cue instead of letting the list look cut off.
  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollMore(el.scrollHeight - el.scrollTop - el.clientHeight > 8);
  }, []);

  useEffect(() => {
    updateScrollState();
  }, [tables, loading, updateScrollState]);

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40">
      <div
        className="bg-white rounded-t-3xl w-full max-w-md shadow-xl flex flex-col"
        style={{ height: "88vh", maxHeight: "88vh", paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-1 flex-shrink-0">
          <div>
            <h3 className="text-base font-bold text-gray-900">Assign a Table</h3>
            {!loading && tables.length > 0 && (
              <p className="text-xs text-gray-400 mt-0.5">{tables.length} tables total</p>
            )}
          </div>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Scrollable table grid */}
        <div className="relative flex-1 min-h-0">
          <div
            ref={scrollRef}
            onScroll={updateScrollState}
            className="h-full overflow-y-auto px-5 pt-4"
          >
            {loading ? (
              <p className="text-sm text-gray-400 text-center py-6">Loading…</p>
            ) : tables.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">
                No tables configured.
              </p>
            ) : (
              <div className="grid grid-cols-3 gap-3 pb-4">
                {tables.map((table) => {
                  const isFree = table.status === "free";
                  return (
                    <button
                      key={table.id}
                      onClick={() => isFree && onSelect(table)}
                      disabled={!isFree}
                      className={`flex flex-col items-center justify-center rounded-2xl py-4 border-2 transition-colors ${
                        isFree
                          ? "border-green-200 bg-green-50 text-green-700 hover:bg-green-100"
                          : "border-orange-100 bg-orange-50 text-orange-400 cursor-not-allowed"
                      }`}
                    >
                      <span className="text-2xl font-bold">{table.number}</span>
                      {table.label && (
                        <span className="text-xs mt-0.5 truncate max-w-full px-1">
                          {table.label}
                        </span>
                      )}
                      {table.capacity && (
                        <span className="text-xs opacity-70">{table.capacity} seats</span>
                      )}
                      <span className={`text-xs font-semibold mt-1 ${isFree ? "text-green-600" : "text-orange-500"}`}>
                        {isFree ? "Free" : "Busy"}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Fade + hint so it's obvious the grid keeps scrolling */}
          {canScrollMore && (
            <div className="pointer-events-none absolute bottom-0 left-0 right-0 flex flex-col items-center">
              <div className="w-full h-10 bg-gradient-to-t from-white to-transparent" />
              <span className="-mt-1 mb-1 text-[11px] font-medium text-gray-400 bg-white/90 px-2 rounded-full">
                ↓ scroll for more tables
              </span>
            </div>
          )}
        </div>

        {/* Sticky bottom button */}
        <div className="px-5 pt-3 pb-4 flex-shrink-0 border-t border-gray-100">
          <button
            onClick={onNoTable}
            className="w-full py-3 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold"
          >
            Check in without table
          </button>
        </div>
      </div>
    </div>
  );
}

export default TablePickerPopup;
